import { useRef, useState, useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, useNavigate, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import LoginPage from "@/pages/LoginPage";
import Dashboard from "@/pages/Dashboard";
import Campaigns from "@/pages/Campaigns";
import CampaignDetail from "@/pages/CampaignDetail";
import OptimizationHistory from "@/pages/OptimizationHistory";
import SettingsPage from "@/pages/SettingsPage";
import OAuthCallback from "@/pages/OAuthCallback";
import AdminUsersPage from "@/pages/AdminUsersPage";
import PendingApprovalPage from "@/pages/PendingApprovalPage";
import ForgotPasswordPage from "@/pages/ForgotPasswordPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import PrivacyPolicyPage from "@/pages/PrivacyPolicyPage";
import TermsOfServicePage from "@/pages/TermsOfServicePage";
import AboutPage from "@/pages/AboutPage";
import AffiliatesPage from "@/pages/AffiliatesPage";
import AffiliatePortalPage from "@/pages/AffiliatePortalPage";
import EarlyAccessPage from "@/pages/EarlyAccessPage";
import SimulatorPage from "@/pages/SimulatorPage";
import PricingDemoPage from "@/pages/PricingDemoPage";
import NewAgentsPage from "@/pages/NewAgentsPage";
import AdvisorPage from "@/pages/AdvisorPage";
import SimHome from "@/sim/SimHome";
import SimLanding from "@/sim/SimLanding";
import SimResults from "@/sim/SimResults";
import SimAdmin from "@/sim/SimAdmin";
import { SimGate } from "@/sim/SimGate";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Auth Callback Component
const AuthCallback = () => {
  const hasProcessed = useRef(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processAuth = async () => {
      const hash = window.location.hash;
      const sessionId = new URLSearchParams(hash.substring(1)).get("session_id");
      const urlParams = new URLSearchParams(window.location.search);
      const subscribePlan = urlParams.get("subscribe") || localStorage.getItem("roas_subscribe_plan");
      const isDemo = urlParams.get("demo") === "true" || localStorage.getItem("roas_demo_intent") === "true";
      
      // Clear intents
      localStorage.removeItem("roas_demo_intent");
      localStorage.removeItem("roas_subscribe_plan");

      if (!sessionId) {
        navigate("/login");
        return;
      }

      try {
        const response = await fetch(`${API}/auth/session`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ session_id: sessionId }),
        });

        if (!response.ok) throw new Error("Auth failed");
        
        const user = await response.json();

        // Store session token in localStorage (minimize cookies)
        if (user.session_token) {
          localStorage.setItem("roas_session_token", user.session_token);
        }

        if (subscribePlan) {
          try {
            const checkoutResp = await fetch(`${API}/subscriptions/checkout`, {
              method: "POST", credentials: "include",
              headers: {
                "Content-Type": "application/json",
                ...(user.session_token ? { "Authorization": `Bearer ${user.session_token}` } : {}),
              },
              body: JSON.stringify({ plan_id: subscribePlan, origin_url: window.location.origin }),
            });
            if (checkoutResp.ok) {
              const data = await checkoutResp.json();
              window.location.href = data.url;
              return;
            }
          } catch {}
          navigate("/pricing-demo", { replace: true });
        } else if (isDemo) {
          // Create demo session with the real Google email
          const demoResp = await fetch(`${API}/auth/demo-session`, {
            method: "POST", credentials: "include",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ email: user.email, name: user.name, picture: user.picture }),
          });
          if (demoResp.ok) {
            const demoData = await demoResp.json();
            localStorage.setItem("roas_session_token", demoData.token);
          }
          window.location.href = "/campaigns";
        } else {
          window.location.href = "/campaigns";
        }
      } catch (error) {
        console.error("Auth error:", error);
        navigate("/login");
      }
    };

    processAuth();
  }, [navigate]);

  return (
    <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
      <div className="spinner" />
    </div>
  );
};

// Post-login handler for first-party Google OAuth (reads #token=... set by backend callback)
const AuthComplete = () => {
  const navigate = useNavigate();
  const hasRun = useRef(false);

  useEffect(() => {
    if (hasRun.current) return;
    hasRun.current = true;

    const params = new URLSearchParams(window.location.hash.substring(1));
    const token = params.get("token");
    const subscribe = params.get("subscribe");
    const demo = params.get("demo");
    let next = params.get("next") || "/campaigns";
    if (!next.startsWith("/") || next.startsWith("//")) next = "/campaigns";

    if (!token) {
      navigate("/login");
      return;
    }
    localStorage.setItem("roas_session_token", token);

    const finish = async () => {
      if (subscribe) {
        try {
          const resp = await fetch(`${API}/subscriptions/checkout`, {
            method: "POST",
            credentials: "include",
            headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
            body: JSON.stringify({ plan_id: subscribe, origin_url: window.location.origin }),
          });
          if (resp.ok) {
            const d = await resp.json();
            window.location.href = d.url;
            return;
          }
        } catch (e) {}
        window.location.href = "/pricing-demo";
        return;
      }
      if (demo) {
        try {
          const me = await fetch(`${API}/auth/me`, {
            credentials: "include",
            headers: { Authorization: `Bearer ${token}` },
          });
          if (me.ok) {
            const u = await me.json();
            const dr = await fetch(`${API}/auth/demo-session`, {
              method: "POST",
              credentials: "include",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ email: u.email, name: u.name, picture: u.picture }),
            });
            if (dr.ok) {
              const dd = await dr.json();
              if (dd.token) localStorage.setItem("roas_session_token", dd.token);
            }
          }
        } catch (e) {}
        window.location.href = "/campaigns";
        return;
      }
      window.location.href = next;
    };

    finish();
  }, [navigate]);

  return (
    <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
      <div className="spinner" />
    </div>
  );
};

// Protected Route Component
const ProtectedRoute = ({ children, allowPending = false }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(null);
  const [user, setUser] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    // If user data was passed from AuthCallback, use it
    if (location.state?.user) {
      setUser(location.state.user);
      setIsAuthenticated(true);
      return;
    }

    const checkAuth = async () => {
      try {
        const token = localStorage.getItem("roas_session_token");
        if (!token) {
          // Also check cookie via credentials:include
          const response = await fetch(`${API}/auth/me`, { credentials: "include" });
          if (!response.ok) throw new Error("Not authenticated");
          const userData = await response.json();
          setUser(userData);
          setIsAuthenticated(true);
          return;
        }
        const response = await fetch(`${API}/auth/me`, {
          credentials: "include",
          headers: { "Authorization": `Bearer ${token}` },
        });
        if (!response.ok) throw new Error("Not authenticated");
        const userData = await response.json();
        setUser(userData);
        setIsAuthenticated(true);
      } catch (error) {
        setIsAuthenticated(false);
        localStorage.removeItem("roas_session_token");
        if (location.pathname !== "/login") {
          navigate("/login");
        }
      }
    };

    checkAuth();
  }, [location.state, navigate]);

  if (isAuthenticated === null) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
        <div className="spinner" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Check if user is pending approval and redirect to pending page
  // Allow demo users through, and don't redirect if already on pending-approval page
  if (!allowPending && user && user.registered === "N" && !user.is_demo && location.pathname !== "/pending-approval") {
    return <Navigate to="/pending-approval" replace state={{ user }} />;
  }

  // Pass user as prop to children
  return children({ user });
};

// App Router Component
const AppRouter = () => {
  const location = useLocation();
  // When served from the sim.* subdomain, the simulator is the root experience.
  const isSimHost = typeof window !== "undefined" && /(^|\.)sim\./i.test(window.location.hostname);
  const homePath = isSimHost ? "/sim" : "/campaigns";

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  // Check URL fragment synchronously for session_id (prevents race conditions).
  // Skip the ROAS-AI callback when it's a sim sign-in (tagged ?sim_auth=1) — SimGate handles that.
  const isSimAuthCallback = new URLSearchParams(location.search).get("sim_auth") === "1";
  if (location.hash?.includes("session_id=") && !isSimAuthCallback) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/auth/complete" element={<AuthComplete />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/privacy" element={<PrivacyPolicyPage />} />
      <Route path="/terms" element={<TermsOfServicePage />} />
      <Route path="/about" element={<AboutPage />} />
      <Route path="/affiliates" element={<AffiliatesPage />} />
      <Route path="/affiliates/portal" element={<AffiliatePortalPage />} />
      <Route path="/early" element={<EarlyAccessPage />} />
      <Route path="/pricing-demo" element={<PricingDemoPage />} />
      <Route path="/new-agents" element={<NewAgentsPage />} />
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            {({ user }) => <Dashboard user={user} />}
          </ProtectedRoute>
        }
      />
      <Route
        path="/campaigns"
        element={
          <ProtectedRoute>
            {({ user }) => <Campaigns user={user} />}
          </ProtectedRoute>
        }
      />
      <Route
        path="/campaigns/:campaignId"
        element={
          <ProtectedRoute>
            {({ user }) => <CampaignDetail user={user} />}
          </ProtectedRoute>
        }
      />
      <Route
        path="/optimization"
        element={
          <ProtectedRoute>
            {({ user }) => <OptimizationHistory user={user} />}
          </ProtectedRoute>
        }
      />
      <Route
        path="/advisor"
        element={
          <ProtectedRoute>
            {({ user }) => <AdvisorPage user={user} />}
          </ProtectedRoute>
        }
      />
      <Route
        path="/simulator"
        element={
          <ProtectedRoute>
            {({ user }) => <SimulatorPage user={user} />}
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings"
        element={
          <ProtectedRoute>
            {({ user }) => <SettingsPage user={user} />}
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/users"
        element={
          <ProtectedRoute>
            {({ user }) => <AdminUsersPage user={user} />}
          </ProtectedRoute>
        }
      />
      <Route
        path="/pending-approval"
        element={
          <ProtectedRoute allowPending={true}>
            {({ user }) => <PendingApprovalPage user={user} />}
          </ProtectedRoute>
        }
      />
      <Route path="/oauth/callback/:platform" element={<OAuthCallback />} />
      {/* Synthetic Audience Simulator — public landing for logged-out visitors, tool for signed-in users */}
      <Route path="/sim" element={<SimGate publicFallback={<SimLanding />}><SimHome /></SimGate>} />
      <Route path="/sim/s/:id" element={<SimGate><SimResults /></SimGate>} />
      <Route path="/sim/admin" element={<SimGate requireAdmin><SimAdmin /></SimGate>} />
      <Route path="/" element={<Navigate to={homePath} replace />} />
      <Route path="*" element={<Navigate to={homePath} replace />} />
    </Routes>
  );
};

function App() {
  // Track affiliate referrals from ?ref= parameter
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const ref = params.get("ref");
    if (ref) {
      localStorage.setItem("roas_ref", ref);
      // Track click
      fetch(`${API}/affiliates/track/click/${ref}`, { method: "POST" }).catch(() => {});
    }
  }, []);

  return (
    <div className="app-container">
      <div className="noise-overlay" />
      <BrowserRouter>
        <AppRouter />
      </BrowserRouter>
      <Toaster position="top-right" />
    </div>
  );
}

export default App;
